import { Container, Graphics, Sprite, Text, TilingSprite, type Texture } from 'pixi.js';
import { parseSquare } from '../../logic/road/square';
import { glowTexture } from './glow';
import { animate, COLOURS, displayStyle, ease, fadeTexture, uiStyle } from '../board/ui';

/** The painted pieces the road is made of; any can be missing while the
 *  road's art loads (the strip redraws when {@link RoadStrip.redraw} runs). */
export interface RoadArt {
  /** `path` is the world's own road surface, if it has one; `cobbles` the
   *  shared one (Forest's) that stands in for it. `gift` is the gift
   *  square's (the Sweet Trail's gift). */
  (name: 'lamp' | 'bezel' | 'cobbles' | 'path' | 'coin' | 'gem' | 'spin' | 'collector' | 'gift'): Texture | null;
}

export interface RoadStripState {
  squares: readonly string[];
  position: number;
  meter: number;
  cost: number;
}

/** After a spin the road waits this long, then rolls for {@link ROLL_MS};
 *  a prize it reaches has shrunk into the token about {@link TAKEN_MS} in. */
export const ROLL_DELAY_MS = 380;
export const ROLL_MS = 720;
export const TAKEN_MS = ROLL_DELAY_MS + Math.round(ROLL_MS * 0.6);

/** What the road ends in: the Wheel Run's lamp by default, or other art
 *  (looked up as it's drawn when given as a function, so it can load
 *  later). */
export interface RoadEnd {
  art: 'lamp' | Texture | (() => Texture | null);
  caption: string;
}

/**
 * The road to the next Wheel Run, in Pixi: the player's token (the slot's
 * collector symbol) stays put on the left and a gold cobbled road rolls
 * towards it, a little for every collector landed, so the next prize
 * closes in and is taken when it arrives. Prizes sit on brass bezels; the
 * road fades in from under the token and out past the right edge. The gift
 * square mid-lap shows the trail's gift, and opens its game when reached.
 */
export class RoadStrip extends Container {
  private readonly token = new Container();
  private readonly tokenGlow = new Sprite(glowTexture());
  private readonly tokenArt = new Sprite();
  private readonly viewport = new Container();
  private readonly viewportMask = new Sprite();
  private readonly strip = new Container();
  private cobbles: TilingSprite | null = null;
  private squares: Container[] = [];
  private readonly toGo: Text;
  private readonly pop: Text;
  private state: RoadStripState | null = null;
  private drawn = { position: 0, meter: 0 };
  private offset = 0;
  private width_ = 300;
  private size = { tokenSq: 44, sq: 36, compact: true };
  private rollId = 0;

  constructor(private readonly art: RoadArt, private readonly end: RoadEnd = { art: 'lamp', caption: 'Wheel Run' }) {
    super();
    this.tokenGlow.anchor.set(0.5);
    this.tokenGlow.tint = 0xffd678;
    this.tokenGlow.alpha = 0.55;
    this.tokenArt.anchor.set(0.5);
    this.token.addChild(this.tokenGlow, this.tokenArt);
    this.toGo = new Text({ text: '', style: uiStyle(12, COLOURS.gold) });
    this.toGo.anchor.set(0.5, 0);
    this.pop = new Text({ text: '', style: displayStyle(16) });
    this.pop.anchor.set(0.5, 1);
    this.pop.visible = false;
    this.viewport.addChild(this.strip);
    this.viewport.mask = this.viewportMask;
    this.addChild(this.viewport, this.viewportMask, this.token, this.toGo, this.pop);
  }

  /** Where the player's token sits, in the strip's space (a host can
   *  make it breathe, as the trail does while its bonus waits). */
  get tokenCentre(): { x: number; y: number } {
    return { x: this.tokenArt.x, y: this.tokenArt.y };
  }

  /** Scale the token and its glow (1 is its size). */
  setTokenScale(scale: number): void {
    this.token.pivot.set(this.tokenArt.x, this.tokenArt.y);
    this.token.position.set(this.tokenArt.x, this.tokenArt.y);
    this.token.scale.set(scale);
  }

  /** The strip's height for a token size. */
  static heightFor(tokenSq: number, sq: number, compact: boolean): number {
    return Math.max(tokenSq, sq + (compact ? 13 : 15) + 4) + (compact ? 14 : 16);
  }

  layoutStrip(width: number, tokenSq: number, sq: number, compact: boolean): void {
    this.width_ = Math.max(120, width);
    this.size = { tokenSq, sq, compact };
    this.redraw();
  }

  /**
   * A new road state. A spin's move (`rolled`) rolls the road towards the
   * token once the reels have landed; anything else snaps. `hop` means the
   * token reached a prize.
   */
  update(state: RoadStripState, options: { rolled: boolean; collected?: number; hop?: boolean; delayMs?: number } = { rolled: false }): void {
    const first = this.state === null;
    const levelChanged = this.state !== null && this.state.squares.join() !== state.squares.join();
    this.state = state;
    if (first || levelChanged || !options.rolled || state.position < this.drawn.position) {
      this.drawn = { position: state.position, meter: state.meter };
      this.redraw();
      return;
    }
    if (options.collected && options.collected > 0) this.showPop(options.collected);
    const id = ++this.rollId;
    setTimeout(() => {
      // A newer state took over, or the slot was left before the roll began.
      if (id !== this.rollId || this.destroyed) return;
      const from = this.offset;
      const before = this.drawn.position;
      this.drawn = { position: state.position, meter: state.meter };
      const to = this.offsetFor(this.drawn);
      this.updateToGo();
      void animate(ROLL_MS, (t) => {
        this.offset = from + (to - from) * ease.outCubic(t);
        this.strip.x = this.lead() - this.offset;
        // Prizes reached on this roll shrink into the token.
        this.squares.forEach((sq, i) => {
          const index = i + 1;
          if (index > before && index <= this.drawn.position) {
            const k = Math.max(0, (t - 0.35) / 0.65);
            sq.alpha = 1 - k;
            sq.scale.set(1 - 0.7 * k);
          }
        });
      });
      if (options.hop) this.gulp();
    }, options.delayMs ?? ROLL_DELAY_MS);
  }

  /** The middle of the token, where the road's prizes are taken (canvas
   *  pixels). */
  get tokenPoint(): { x: number; y: number } {
    return this.tokenArt.getGlobalPosition();
  }

  /** Redraw after the art has loaded. */
  redraw(): void {
    if (!this.state) return;
    const { tokenSq, sq, compact } = this.size;
    const { squares, cost } = this.state;
    const lastIndex = squares.length - 1;
    const unit = this.unit();
    const pitch = cost * unit + sq;
    const roadY = sq / 2 + 6;
    const band = Math.round(sq * 0.36);
    const lead = this.lead();
    const viewW = this.width_ - tokenSq + lead;

    // Token.
    const collector = this.atRun() ? this.endTexture() : this.art('collector');
    if (collector) this.tokenArt.texture = collector;
    this.tokenArt.width = this.tokenArt.height = tokenSq;
    this.tokenArt.position.set(tokenSq / 2, roadY);
    this.tokenGlow.width = this.tokenGlow.height = tokenSq * 1.5;
    this.tokenGlow.position.copyFrom(this.tokenArt.position);

    // The road and its prizes.
    this.viewport.x = tokenSq - lead;
    this.strip.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.squares = [];
    // The world's own surface is painted for the panel; the shared cobbles
    // are toned down to sit on it.
    const own = this.art('path');
    const cobbleTexture = own ?? this.art('cobbles');
    this.cobbles = null;
    if (cobbleTexture) {
      this.cobbles = new TilingSprite({ texture: cobbleTexture, width: (lastIndex + 1) * pitch + lead + viewW + 400, height: band });
      const scale = band / Math.max(1, cobbleTexture.height);
      this.cobbles.tileScale.set(scale, scale);
      this.cobbles.position.set(-lead, roadY - band / 2);
      this.cobbles.tint = own ? 0xffffff : 0xd8cfc0;
      // The board's own drop shadow (black at 35%, a little below), scaled
      // to the road: without it the road read as the background carried on.
      const shadow = new Graphics().rect(0, 0, this.cobbles.width, band).fill({ color: 0x000000, alpha: 0.35 });
      shadow.position.set(-lead, roadY - band / 2 + Math.max(2, Math.round(band * 0.25)));
      this.strip.addChild(shadow, this.cobbles);
    }
    for (let i = 1; i <= lastIndex; i++) {
      const square = this.buildSquare(squares[i]!, i === lastIndex);
      square.position.set(i * pitch - sq / 2, roadY);
      const taken = i <= this.drawn.position;
      square.alpha = taken ? 0 : 1;
      this.strip.addChild(square);
      this.squares.push(square);
    }
    this.offset = this.offsetFor(this.drawn);
    this.strip.x = lead - this.offset;

    // Fade in from under the token, out towards the right edge.
    this.viewportMask.texture = fadeTexture(Math.min(0.4, (lead * 2) / viewW), 0.3);
    this.viewportMask.position.set(this.viewport.x, 0);
    this.viewportMask.width = viewW;
    this.viewportMask.height = RoadStrip.heightFor(tokenSq, sq, compact);

    this.toGo.style = uiStyle(compact ? 11 : 12, COLOURS.gold);
    this.toGo.position.set(tokenSq / 2, roadY + tokenSq / 2 + 1);
    this.pop.position.set(tokenSq + 4, roadY - tokenSq / 2 + 2);
    this.updateToGo();
  }

  private buildSquare(label: string, last: boolean): Container {
    const { sq, compact } = this.size;
    const c = new Container();
    const square = parseSquare(label);
    const isEnd = square.kind === 'run' || (last && square.kind === 'start');
    const size = isEnd ? Math.round(sq * 1.12) : sq;
    if (isEnd) {
      const glow = new Sprite(glowTexture());
      glow.anchor.set(0.5);
      glow.tint = 0xffd36b;
      glow.width = glow.height = size * 1.7;
      glow.alpha = 0.7;
      c.addChild(glow);
      const art = this.endTexture();
      if (art) {
        const s = new Sprite(art);
        s.anchor.set(0.5);
        s.width = s.height = size;
        c.addChild(s);
      }
    } else {
      const bezel = this.art('bezel');
      if (bezel) {
        const b = new Sprite(bezel);
        b.anchor.set(0.5);
        b.width = b.height = size;
        c.addChild(b);
      }
      const gift = square.kind === 'gift';
      const icon = this.art(square.kind === 'coins' ? 'coin' : square.kind === 'gems' ? 'gem' : gift ? 'gift' : 'spin');
      if (icon) {
        const s = new Sprite(icon);
        s.anchor.set(0.5);
        // The gift is the square's prize: a little bigger than a coin.
        s.width = s.height = Math.round(size * (gift ? 0.62 : 0.5));
        c.addChild(s);
      }
      c.label = gift ? 'road-gift' : '';
    }
    const caption = new Text({
      text: isEnd
        ? this.end.caption
        : square.kind === 'gift' ? 'Gift'
        : square.kind === 'fs' ? `${square.amount} free`
        : `+${square.amount}`,
      style: isEnd ? displayStyle(compact ? 12 : 13) : uiStyle(compact ? 11 : 13),
    });
    caption.anchor.set(0.5, 0);
    caption.y = size / 2 + 2;
    c.addChild(caption);
    return c;
  }

  private endTexture(): Texture | null {
    const art = this.end.art;
    if (art === 'lamp') return this.art('lamp');
    return typeof art === 'function' ? art() : art;
  }

  private atRun(): boolean {
    return !!this.state && this.drawn.position >= this.state.squares.length - 1;
  }

  private unit(): number {
    const cost = this.state?.cost ?? 20;
    return Math.max(3, Math.min(8, Math.floor((this.size.compact ? 76 : 100) / cost)));
  }

  private lead(): number {
    return Math.round(this.size.tokenSq * 0.3);
  }

  private offsetFor(drawn: { position: number; meter: number }): number {
    if (!this.state) return 0;
    const last = this.state.squares.length - 1;
    const pitch = this.state.cost * this.unit() + this.size.sq;
    const meter = drawn.position >= last ? 0 : Math.max(0, Math.min(this.state.cost - 1, drawn.meter));
    return Math.min(drawn.position, last) * pitch + meter * this.unit();
  }

  private updateToGo(): void {
    if (!this.state) return;
    const remaining = this.state.cost - Math.max(0, Math.min(this.state.cost - 1, this.drawn.meter));
    this.toGo.text = this.atRun() ? '' : `${remaining} to go`;
    const collector = this.atRun() ? this.endTexture() : this.art('collector');
    if (collector) this.tokenArt.texture = collector;
  }

  private showPop(amount: number): void {
    this.pop.text = `+${amount}`;
    this.pop.visible = true;
    const y0 = this.pop.y;
    void animate(1300, (t) => {
      this.pop.alpha = t < 0.15 ? t / 0.15 : t > 0.8 ? 1 - (t - 0.8) / 0.2 : 1;
      this.pop.y = y0 - 14 * t;
    }).then(() => {
      this.pop.visible = false;
      this.pop.y = y0;
    });
  }

  private gulp(): void {
    const base = this.size.tokenSq;
    void animate(520, (t) => {
      const s = 1 + 0.18 * Math.sin(Math.min(1, t * 1.4) * Math.PI);
      this.tokenArt.width = this.tokenArt.height = base * s;
    });
  }
}
